import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";
import { useSelector, useDispatch } from "react-redux";

import { useCallbackPrompt } from "@hooks/useCallbackPrompt";
import { EtkTable } from "@components/Common/EtkTable";

import { store } from "../../../../components/VerticalLayout/store";
import { useAuthHeader } from "../../../../api/headersKeycloak";

import { RootState, AppDispatch } from "../../../../redux/store";
import {
    setIsActionsClicked,
    setIsEdited,
    setIsSupportCodeValid,
} from "../../../../redux/slices/fidelitySlice";
import { useFidelityNavigationGuard } from "../FidelityNavigationGuardContext";

import { NumberActiveAndArchive, Selected } from "./helpers";
import { useEtkColumns } from "./useEtkColumns";
import TableToolbar from "./TableToolbar";
import FidelityModals from "./FidelityModals";
import FidelityFormWrapper from "./FidelityFormWrapper";

import "./index.css";
import "@assets/swal.css";
import "@assets/theme.css";
import "react-toastify/dist/ReactToastify.css";

type FidelityFranchiseProps = {
    isFidelityArchived: boolean;
    setIsFidelityArchived: React.Dispatch<React.SetStateAction<boolean>>;
    setLimit: React.Dispatch<React.SetStateAction<number>>;
};

export default function FidelityFranchise({
    isFidelityArchived,
    setIsFidelityArchived,
    setLimit,
}: FidelityFranchiseProps) {
    const { t } = useTranslation();
    const getAuthHeader = useAuthHeader();
    const didMountRef = React.useRef(false);
    const { oneShop, userID, franchiseID, shopID } = useSnapshot(store);
    const fidelityData = useSelector((s: RootState) => s.fidelity.fidelityData);
    const isActionsClicked = useSelector(
        (s: RootState) => s.fidelity.isActionsClicked
    );
    const isEdited = useSelector((s: RootState) => s.fidelity.isEdited);
    const modify = useSelector((s: RootState) => s.fidelity.modify);
    const {
        setOnConfirm,
        setOnCancel,
        runOnConfirm,
        runOnCancel,
    } = useFidelityNavigationGuard();
    const isLoading = useSelector((s: RootState) => s.fidelity.isLoading);
    const key = useSelector((s: RootState) => s.fidelity.key);
    const dispatch = useDispatch<AppDispatch>();

    const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(
        isEdited
    );

    const [isConsult, setIsConsult] = React.useState<boolean>(false);
    const [
        isAddNewFidelityButtonClicked,
        setIsAddNewFidelityButtonClicked,
    ] = useState(false);
    const [dataIsUpdated, setDataIsUpdated] = React.useState<boolean | number>(
        false
    );
    const [filterActive, setLocalFilterActive] = React.useState<boolean>(false);
    const [
        isInInitializationModalOpened,
        setIsInInitializationModalOpened,
    ] = React.useState<boolean>(false);
    const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
    const [isHistoryModalOpened, setIsHistoryModalOpened] = React.useState<
        boolean
    >(false);
    const [fidelity, setFidelity] = useState<any>([]);
    const [shopId, setShopId] = React.useState<string | number>(
        oneShop ? shopID : "0"
    );
    const [selectedRows, setSelectedRows] = React.useState<FidelityRow[]>([]);
    const [dataTable, setDataTable] = React.useState<
        FidelityDataTable & { data?: FidelityRow[] }
    >({});
    const [isAddFidelityClicked, setIsAddFidelityClicked] = React.useState<
        boolean
    >(false);
    const [fidelityChoiceIndex, setFidelityChoiceIndex] = React.useState<
        number
    >(0);
    const [isModalOpenedSupport, setIsModalOpenedSupport] = React.useState<
        boolean
    >(false);
    const [codeSupport, setCodeSupport] = React.useState<string>("");
    const [isValidCode, setIsValidCode] = React.useState(false);

    const i18nextLng = localStorage.getItem("i18nextLng") || "fr";
    const urlArchive = `${process.env.REACT_APP_API_V2_URL}/settings/general/fidelity?userId=${userID}&shopId=${shopId}&franchiseId=${franchiseID}`;
    const urlActive = `${process.env.REACT_APP_API_V2_URL}/settings/general/fidelity?userId=${userID}&shopId=${shopId}&franchiseId=${franchiseID}&type=activated`;

    const requestHeaders = React.useMemo(
        () =>
            ({ "Accept-Language": i18nextLng, ...getAuthHeader } as Record<
                string,
                string
            >),
        [i18nextLng, getAuthHeader]
    );

    const customColumns = useEtkColumns({
        t,
        setShopId,
        setFidelity,
        setIsModalOpened,
        isModalOpened,
        setDataIsUpdated,
        setSelectedRows,
        isFidelityArchived,
        isAddFidelityClicked,
        setIsAddFidelityClicked,
        selectedRows,
        setLocalFilterActive,
        setIsModalOpenedSupport,
        setIsConsult,
        setCodeSupport,
    });

    React.useEffect(() => {
        if (filterActive) setIsAddFidelityClicked(false);
    }, [filterActive]);

    React.useEffect(() => {
        Selected(selectedRows, dataTable, oneShop);
    }, [selectedRows, dataTable, t, oneShop]);

    React.useEffect(() => {
        NumberActiveAndArchive(dataTable);
        // eslint-disable-next-line
    }, [dataTable]);

    React.useEffect(() => {
        setOnConfirm(confirmNavigation as () => void);
        setOnCancel(cancelNavigation as () => void);
        dispatch(setIsActionsClicked(showPrompt as boolean));
        // Why: `dispatch`, `setOnConfirm`, `setOnCancel` are stable identities
        // (React-Redux + Context); only the navigation-prompt state should retrigger.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPrompt, confirmNavigation, cancelNavigation]);

    React.useEffect(() => {
        setIsAddFidelityClicked(false);
        if (didMountRef.current) {
            setDataIsUpdated(true);
        } else {
            didMountRef.current = true;
        }
    }, [t]);

    const isSupportCodeValidDerived =
        !isModalOpenedSupport && !isValidCode && codeSupport !== "";

    React.useEffect(() => {
        dispatch(setIsSupportCodeValid(isSupportCodeValidDerived));
        // Why: `dispatch` is stable per React-Redux contract.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSupportCodeValidDerived]);

    return (
        <React.Fragment key={key}>
            <div
                style={{
                    display: "grid",
                    minHeight: oneShop ? "650px" : "600px",
                    gridTemplateColumns: isAddFidelityClicked
                        ? "2fr 1fr "
                        : "auto",
                    gridColumnGap: "4px",
                    border: 0,
                }}
            >
                <EtkTable
                    refreshKey={`${dataIsUpdated}`}
                    url={isFidelityArchived ? urlArchive : urlActive}
                    headers={requestHeaders}
                    customColumns={customColumns}
                    canExpand={false}
                    canSelect
                    customSelect
                    onDataChange={setDataTable}
                    onSelectionChange={setSelectedRows}
                    toolbarRight={
                        <TableToolbar
                            modify={modify}
                            oneShop={oneShop}
                            isEdited={isEdited}
                            isLoading={isLoading}
                            isAddFidelityClicked={isAddFidelityClicked}
                            setIsAddFidelityClicked={setIsAddFidelityClicked}
                            isFidelityArchived={isFidelityArchived}
                            setIsFidelityArchived={setIsFidelityArchived}
                            isModalOpened={isModalOpened}
                            setIsModalOpened={setIsModalOpened}
                            setIsModalOpenedSupport={setIsModalOpenedSupport}
                            setIsInInitializationModalOpened={
                                setIsInInitializationModalOpened
                            }
                            setLocalFilterActive={setLocalFilterActive}
                            setFidelity={setFidelity}
                            setFidelityChoiceIndex={setFidelityChoiceIndex}
                            setDataIsUpdated={setDataIsUpdated}
                            fidelity={fidelity}
                            fidelityData={fidelityData}
                            selectedRows={selectedRows}
                            setLimit={setLimit}
                            t={t}
                        />
                    }
                    showSearch
                    showFilters
                    canResize
                    canMovedCheckboxLeftOnExpand
                    filterActive={filterActive}
                    setLocalFilterActive={setLocalFilterActive}
                    dataIsUpdated={dataIsUpdated}
                    setDataIsUpdated={setDataIsUpdated}
                    minHeight={oneShop ? "500px" : "450px"}
                    maxHeight={oneShop ? "500px" : "450px"}
                    name="fidelityForm"
                />
                {isAddFidelityClicked ? (
                    <FidelityFormWrapper
                        isAddFidelityClicked={isAddFidelityClicked}
                        setIsAddFidelityClicked={setIsAddFidelityClicked}
                        setDataIsUpdated={setDataIsUpdated}
                        dataFidelity={fidelity}
                        isAddNewFidelityButtonClicked={
                            isAddNewFidelityButtonClicked
                        }
                        setDataFidelity={setFidelity}
                        setLocalFilterActive={setLocalFilterActive}
                        fidelityChoiceIndex={fidelityChoiceIndex}
                        setFidelityChoiceIndex={setFidelityChoiceIndex}
                        setIsConsult={setIsConsult}
                        isConsult={isConsult}
                    />
                ) : null}
            </div>

            <FidelityModals
                isActionsClicked={isActionsClicked}
                setIsActionsClicked={(v: boolean) =>
                    dispatch(setIsActionsClicked(v))
                }
                onConfirm={runOnConfirm}
                onCancel={runOnCancel}
                setIsEdited={(v: boolean) => dispatch(setIsEdited(v))}
                isModalOpened={isModalOpened}
                setIsModalOpened={setIsModalOpened}
                isInInitializationModalOpened={isInInitializationModalOpened}
                setIsInInitializationModalOpened={
                    setIsInInitializationModalOpened
                }
                isAddNewFidelityButtonClicked={isAddNewFidelityButtonClicked}
                setIsAddNewFidelityButtonClicked={
                    setIsAddNewFidelityButtonClicked
                }
                isHistoryModalOpened={isHistoryModalOpened}
                setIsHistoryModalOpened={setIsHistoryModalOpened}
                setDataIsUpdated={setDataIsUpdated}
                isModalOpenedSupport={isModalOpenedSupport}
                setIsModalOpenedSupport={setIsModalOpenedSupport}
                codeSupport={codeSupport}
                setCodeSupport={setCodeSupport}
                isValidCode={isValidCode}
                setIsValidCode={setIsValidCode}
                isLoading={isLoading}
                t={t}
            />
        </React.Fragment>
    );
}
